import { Avatar, Box } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import {
  CompanyCustomBenefitAssignmentEmployee,
  EmployeeBenefitSettingsShort,
  EmployeeListItem,
  Location,
  Department,
  CostCenter
} from '@pxtra-client-lib-legacy/gen';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { TableRowSelector } from '../shared/selection-checkbox';
import { BENEFIT_KEYS } from '../shared/utils';
import { AssignmentTypeCell } from './cells/assignmentType';
import { BudgetCell } from './cells/budget';
import { CurrentBenefitsCell } from './cells/currentBenefits';
import { EditingStatusCell } from './cells/editingStatus';
import { HintsCell } from './cells/hints';
import { CustomBenefitActionsCell } from './cells/actions';
import { CustomBenefitGroupsCell } from './cells/groups';
import { getAssignmentType } from './utils';
import {
  useCompanyGroups,
  useCompanyLocations,
  useCompanyDepartments,
  useCompanyCostCenters
} from '../../../../queries/company';
import { useStore } from '../../../../hooks/useStore';

const columnHelper = createColumnHelper<CompanyCustomBenefitAssignmentEmployee>();

export function useCustomBenefitAssignmentColumns(benefitName: string) {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? '';
  const { data: groups } = useCompanyGroups(companyId);
  const { data: locations } = useCompanyLocations(companyId);
  const { data: departments } = useCompanyDepartments(companyId);
  const { data: costCenters } = useCompanyCostCenters(companyId);

  return useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <TableRowSelector className="-mr-16" table={table} pageOnly={false} />
        ),
        cell: ({ row }) => <TableRowSelector className="-mr-16" row={row} />
      }),

      columnHelper.accessor('last_name', {
        id: 'employeeNamePosition',
        header: t('common:EMPLOYEES'),
        cell: (info) => (
          <div className="flex items-center gap-3">
            <Avatar
              variant="rounded"
              sx={{
                backgroundColor: 'white',
                width: 40,
                height: 40,
                borderRadius: '.75rem'
              }}
              src={info.row.original.profile_picture}
              alt={`${info.row.original.first_name} ${info.row.original.last_name}`}
              imgProps={{ style: { objectFit: 'contain' } }}
            />
            <div className="flex flex-col text-midnight-main">
              <div className="text-base font-medium">
                {info.row.original.first_name} {info.row.original.last_name}
              </div>
              <div className="text-sm">{info.row.original.position_name}</div>
            </div>
          </div>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false
      }),

      columnHelper.accessor('member_groups', {
        id: 'groups',
        header: t('company:GROUPS'),
        cell: CustomBenefitGroupsCell,
        meta: {
          filterVariant: 'select',
          filterOptions: [
            { value: 'not_assigned', text: t('company:GROUPS_NOT_ASSIGNED') },
            ...(groups?.results.map((group) => ({
              value: group.id,
              text: group.name
            })) ?? [])
          ]
        },
        filterFn: (row, columnId, filterValue) => {
          const memberGroups = row.getValue(columnId);
          if (!filterValue) return true;
          const groupArray = Array.isArray(memberGroups) ? memberGroups : [];
          if (filterValue === 'not_assigned') return groupArray.length === 0;
          return groupArray.some((group: { id: string }) => group.id === filterValue);
        }
      }),

      columnHelper.display({
        id: 'currentBenefits',
        header: t('custom:other_benefits_column'),
        cell: CurrentBenefitsCell,
        enableSorting: false
      }),

      columnHelper.display({
        id: 'plus',
        header: '',
        cell: () => <Box fontWeight="bolder">+</Box>,
        enableSorting: false,
        size: 30
      }),

      columnHelper.display({
        id: 'customBenefitBudget',
        header: benefitName,
        cell: BudgetCell,
        enableSorting: false,
        size: 140
      }),

      columnHelper.accessor((row) => getAssignmentType(row), {
        id: 'assignmentType',
        header: t('custom:assignment'),
        cell: AssignmentTypeCell,
        enableSorting: false,
        size: 110,
        meta: {
          filterVariant: 'select',
          filterOptions: [
            'individual',
            'deactivated_by_CM',
            'assignment_forbidden',
            'invalid_global_assignment',
            'invalid_individual_assignment',
            'global'
          ].map((key) => ({
            value: key,
            text: t(`custom:assignment_label->${key}`)
          }))
        },
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          return getAssignmentType(row.original) === filterValue;
        }
      }),

      columnHelper.display({
        id: 'hints',
        header: t('common:HINTS'),
        cell: HintsCell,
        enableSorting: false,
        size: 90
      }),

      columnHelper.display({
        id: 'editingStatus',
        header: '',
        cell: EditingStatusCell,
        enableSorting: false,
        size: 20
      }),

      columnHelper.display({
        id: 'actions',
        header: '',
        cell: CustomBenefitActionsCell,
        enableSorting: false,
        size: 20
      }),

      // Hidden filter-only columns

      columnHelper.accessor((row) => (row as unknown as EmployeeListItem).location, {
        id: 'location',
        header: t('company:LOCATION'),
        cell: () => null,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: {
          filterVariant: 'select',
          filterOptions:
            locations?.results.map((l: Location) => ({
              value: l.id,
              text: l.name
            })) ?? []
        },
        filterFn: 'includesString'
      }),

      columnHelper.accessor((row) => (row as unknown as EmployeeListItem).department, {
        id: 'department',
        header: t('employee:DEPARTMENT'),
        cell: () => null,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: {
          filterVariant: 'select',
          filterOptions:
            departments?.results.map((d: Department) => ({
              value: d.id,
              text: d.name
            })) ?? []
        },
        filterFn: 'includesString'
      }),

      columnHelper.accessor((row) => (row as unknown as EmployeeListItem).cost_center, {
        id: 'cost_center',
        header: t('employee:COST_CENTER'),
        cell: () => null,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: {
          filterVariant: 'select',
          filterOptions:
            costCenters?.results.map((cc: CostCenter) => ({
              value: cc.id,
              text: cc.name
            })) ?? []
        },
        filterFn: 'includesString'
      }),

      columnHelper.accessor((row) => (row as unknown as EmployeeListItem).status, {
        id: 'status',
        header: t('common:STATUS'),
        cell: () => null,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: {
          filterVariant: 'select',
          filterOptions: [
            { value: 'INACTIVE', text: t('employee:INACTIVE') },
            { value: 'ACTIVE', text: t('employee:ACTIVE') },
            { value: 'PAUSED', text: t('employee:PAUSED') },
            { value: 'RESIGNED', text: t('employee:RESIGNED') },
            { value: 'INVITATION_PENDING_EMAIL', text: t('employee:INVITATION_PENDING_EMAIL') },
            { value: 'INVITATION_PENDING_PDF', text: t('employee:INVITATION_PENDING_PDF') },
            { value: 'DEACTIVATION_DELAYED', text: t('employee:DEACTIVATION_DELAYED') }
          ]
        },
        filterFn: 'equals'
      }),

      columnHelper.accessor((row) => (row as unknown as EmployeeListItem).benefits, {
        id: 'active_benefit',
        header: t('common:ACTIVE_BENEFIT'),
        cell: () => null,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: {
          filterVariant: 'select',
          filterOptions: BENEFIT_KEYS.map((key) => ({
            value: key,
            text: t(`benefits:${key}`)
          }))
        },
        filterFn: (row, _columnId, filterValue): boolean => {
          if (!filterValue) return true;
          const benefits = (row.original as unknown as EmployeeListItem).benefits;
          if (!benefits) return false;
          const setting = benefits[filterValue as keyof EmployeeBenefitSettingsShort];
          return setting?.active === true;
        }
      }),

      columnHelper.accessor(
        (row) => (row as unknown as EmployeeListItem).remaining_benefit_budget_next_month,
        {
          id: 'budget_exhausted',
          header: t('common:BUDGET_EXHAUSTED'),
          cell: () => null,
          enableGlobalFilter: false,
          enableSorting: false,
          meta: {
            filterVariant: 'select',
            filterOptions: [
              { value: 'yes', text: t('common:YES') },
              { value: 'no', text: t('common:NO') }
            ]
          },
          filterFn: (row, _columnId, filterValue): boolean => {
            if (!filterValue) return true;
            const remaining =
              (row.original as unknown as EmployeeListItem).remaining_benefit_budget_next_month ??
              0;
            return filterValue === 'yes' ? remaining <= 0 : remaining > 0;
          }
        }
      ),

      columnHelper.accessor((row) => (row as unknown as EmployeeListItem).entry_date, {
        id: 'entry_date',
        header: t('employee:START_DATE'),
        cell: () => null,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: {
          filterVariant: 'range',
          filterInputType: 'date'
        },
        filterFn: (row, _columnId, filterValue): boolean => {
          const [min, max] = (filterValue as [string, string]) ?? [null, null];
          if (!min && !max) return true;
          const entryDate = (row.original as unknown as EmployeeListItem).entry_date;
          if (!entryDate) return false;
          if (min && entryDate < min) return false;
          if (max && entryDate > max) return false;
          return true;
        }
      })
    ],
    [t, benefitName, groups, locations, departments, costCenters]
  );
}
